import React from 'react';
import { Box } from '@mui/material';
import type { DataTableCellContext, DataTableColumnDef } from './dataTableTypes';
import {
  formatCurrency,
  formatCurrencyFull,
  formatNumberFull,
  formatNumberWithLocale,
} from '../../utils/FormatUtils';
import { EllipsisWithTooltip } from '../common/EllipsisWithTooltip';
import { NumericValueWithTooltip } from '../common/NumericValueWithTooltip';

function getRecordField<TData>(row: TData, key: string | undefined): unknown {
  if (!key) return undefined;
  return (row as Record<string, unknown>)[key];
}

function defaultString(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return String(value);
}

function wrapWithEllipsis<TData>(
  column: DataTableColumnDef<TData>,
  value: string
): React.ReactNode {
  if (column.meta?.noEllipsisTooltip || value === '') {
    return value;
  }
  const ta = column.meta?.align;
  const textAlign = ta === 'right' || ta === 'center' || ta === 'left' ? ta : 'inherit';
  return (
    <Box component="span" sx={{ display: 'block', minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
      <EllipsisWithTooltip text={value} typographySx={{ textAlign }} />
    </Box>
  );
}

/**
 * Body cell resolution: `column.cell` → `column.type` (+ meta) → string from `accessorKey` / `field`.
 */
export function renderDataTableCell<TData>(
  column: DataTableColumnDef<TData>,
  ctx: DataTableCellContext<TData>
): React.ReactNode {
  if (column.cell) {
    return column.cell(ctx);
  }

  const key = (column.accessorKey ?? column.field) as string | undefined;
  const raw = getRecordField(ctx.row, key);

  switch (column.type) {
    case 'number': {
      const n = typeof raw === 'number' ? raw : Number(raw);
      if (Number.isNaN(n)) return wrapWithEllipsis(column, defaultString(raw));
      const maxFrac = column.meta?.maximumFractionDigits ?? 0;
      const display = formatNumberWithLocale(n, maxFrac);
      const ta = column.meta?.align;
      const textAlign = ta === 'right' || ta === 'center' || ta === 'left' ? ta : 'inherit';
      return (
        <Box component="span" sx={{ display: 'block', minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
          <NumericValueWithTooltip
            display={display}
            fullValue={formatNumberFull(n)}
            typographySx={{ textAlign }}
          />
        </Box>
      );
    }
    case 'currency': {
      const n = typeof raw === 'number' ? raw : Number(raw);
      if (Number.isNaN(n)) return wrapWithEllipsis(column, defaultString(raw));
      const display = formatCurrency(n);
      const ta = column.meta?.align;
      const textAlign = ta === 'right' || ta === 'center' || ta === 'left' ? ta : 'inherit';
      return (
        <Box component="span" sx={{ display: 'block', minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
          <NumericValueWithTooltip
            display={display}
            fullValue={formatCurrencyFull(n)}
            typographySx={{ textAlign }}
          />
        </Box>
      );
    }
    case 'date': {
      if (raw == null || raw === '') return '';
      const d = raw instanceof Date ? raw : new Date(String(raw));
      if (Number.isNaN(d.getTime())) return wrapWithEllipsis(column, defaultString(raw));
      return wrapWithEllipsis(column, new Intl.DateTimeFormat(undefined, column.meta?.dateOptions).format(d));
    }
    case 'text':
    default:
      return wrapWithEllipsis(column, defaultString(raw));
  }
}
